import { useState, useEffect } from 'react';
import './PaymentPopup.css';
import { pricingTiers } from '../data/pricing';
import { createSubscription } from '@genfun/core/api/auth';
import { getElapsedTime } from '../components/common';

function CheckIcon() {
    return (
        <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
            <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M1 11L11 1" stroke="#FFB2C6" strokeWidth="2" strokeLinecap="round"/>
        </svg>
    );
}

// with a first-week price the plan shows it against the regular weekly price
function planPrices(tier) {
    if (tier.intro) {
        return {
            current: tier.intro.price,
            original: tier.price.current,
            percent: Math.round((1 - tier.intro.price / tier.price.current) * 100),
        };
    }
    return { current: tier.price.current, original: tier.price.original, percent: tier.sale?.percent };
}

function PlanRow({ tier, isSelected, onClick, showRadio }) {
    const prices = planPrices(tier);
    const hasSale = tier.sale && prices.original;

    return (
        <div
            className={`planRow ${isSelected ? 'planRowSelected' : ''} ${showRadio ? '' : 'planRowNoRadio'}`}
            onClick={onClick}
        >
            {isSelected && <div className="planRowGradientBorder" />}
            <div className="planRowContent">
                {/* Left: name + price */}
                <div className="planInfo">
                    <span className="planName">{tier.name}</span>
                    <div className="planPriceSection">
                        <div className={`planPriceBadge ${isSelected ? 'planPriceBadgeSelected' : ''}`}>
                            <span className="planPrice">${prices.current}</span>
                            <span className="planPricePer">/w</span>
                        </div>
                        {hasSale && (
                            <span className="planOriginal">
                                <s>${prices.original}</s>
                                <span className="planDiscount">−{prices.percent}%</span>
                            </span>
                        )}
                    </div>
                </div>

                {/* Right: features */}
                <div className="planFeatures">
                    <div className="featureLine">
                        <span className="featureValue">{tier.features.photos}</span>
                        <span className="featureLabel">photos</span>
                    </div>
                    <div className="featureLine">
                        <span className="featureValue">{tier.features.videos}</span>
                        <span className="featureLabel">videos</span>
                    </div>
                    <div className="featureLine">
                        <img src={`${process.env.PUBLIC_URL}/w-strikethrough.svg`} className="featureIcon" alt="" />
                        <span className="featureLabel">no watermark</span>
                    </div>
                </div>

                {/* Radio indicator, only when there is more than one plan to pick from */}
                {showRadio && (
                    <div className={`planRadio ${isSelected ? 'planRadioSelected' : ''}`}>
                        {isSelected && <CheckIcon />}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function PaymentPopup({ isOpen, setIsOpen, token }) {
    const [timer, setTimer] = useState("29:59");
    const [loading, setLoading] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState('premium');

    useEffect(() => {
        if (!isOpen) return;
        const interval = setInterval(() => {
            setTimer(getElapsedTime(30 * 60 * 1000));
        }, 1000);
        return () => clearInterval(interval);
    }, [isOpen]);

    if (!isOpen) return null;

    const handleBuy = async () => {
        if (!token || loading) return;
        setLoading(true);
        try {
            const tier = selectedPlanId === 'premium' ? 2 : 1;
            console.log('Creating subscription for tier:', tier, token);
            const result = await createSubscription(token, tier);
            window.location.href = result.url;
        } catch (err) {
            console.error('Subscription error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Filter to only show purchasable tiers (not "coming soon"); Starter is no longer sold
    const purchasableTiers = pricingTiers.filter(t => !t.comingSoon && t.id !== 'starter');
    const selectedTier = purchasableTiers.find(t => t.id === selectedPlanId) || purchasableTiers[0];
    const selectedPrices = planPrices(selectedTier);

    return (
        <div className="paymentPopupOverlay" onClick={() => setIsOpen(false)}>
            <div className="paymentPopupPanel" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="popupHeader">
                    <h2 className="popupTitle">Choose your plan</h2>
                    <button className="popupCloseBtn" onClick={() => setIsOpen(false)}>
                        <CloseIcon />
                    </button>
                </div>
                <p className="popupSaleText">
                    {selectedPrices.percent}% off {selectedTier.intro ? 'for the first week, ends in' : 'sale ends in'} {timer}
                </p>

                {/* Plan rows */}
                <div className="planList">
                    {purchasableTiers.map(tier => (
                        <PlanRow
                            key={tier.id}
                            tier={tier}
                            isSelected={selectedPlanId === tier.id}
                            onClick={() => setSelectedPlanId(tier.id)}
                            showRadio={purchasableTiers.length > 1}
                        />
                    ))}
                </div>

                {/* Buy button */}
                <div className="popupFooter">
                    <button
                        className="buyButton"
                        onClick={handleBuy}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : (selectedTier.intro
                            ? 'Buy'
                            : `Subscribe to ${selectedTier.name} — $${selectedTier.price.current}/w`)}
                    </button>
                </div>
            </div>
        </div>
    );
}
